import { sprite } from '../../../lib';
import { box, byRow, dots, geometry, outlined, round, taper } from '../effect-guns.sprite';

/**
 * Tick-Tock (the Chronomancer set's gun, ADR-0160 amended): a brass blunderbuss with clockwork in it. A copper stock
 * bound in brass; a black iron housing for its works edged in brass, a pressure gauge on its side (an ivory face in a
 * brass rim, its needle trembling) and a little steam vent on its back; a copper flywheel standing over it, its four spokes turning; a brass barrel bound in copper narrowing
 * to a small belled lip. Each shot kicks the flywheel round (a blur of spokes) and throws the gauge's needle to the red.
 */

const BRASS = ['n', 'U', 'Y', 'y'] as const;
const COPPER = ['N', 'u', 'u', 'U'] as const;
const IRON = ['k', '1', '2', '3'] as const;

/** The flywheel's middle and radius. */
const WHEEL = [27.5, 9.5, 6.4] as const;
/** The gauge's middle. */
const GAUGE = [37.5, 19.5] as const;

/**
 * The gun with its flywheel turned `spin` quarters of a quarter turn (its spokes are four: a quarter turn looks the
 * same), its gauge's needle at `needle` (-1 … 3: the red), the spokes doubled when `blur` (spun hard).
 */
const ticktock = (spin: number, needle: number, blur = false) =>
  outlined((c) => {
    // The flywheel behind the body: a copper rim, brass spokes, a boss.
    const [wx, wy, wr] = WHEEL;
    for (let y = 2; y <= 14; y++)
      for (let x = 20; x <= 35; x++) {
        const [dx, dy] = [x + 0.5 - wx, y + 0.5 - wy];
        const r = Math.hypot(dx, dy);
        if (r > wr) continue;
        if (r > wr - 1.6) {
          c.set(x, y, dx + dy < -3 ? 'U' : dx + dy > 3 ? 'N' : 'u');
          continue;
        }
        if (r < 1.3) {
          c.set(x, y, 'y');
          continue;
        }
        const spokes = blur ? 8 : 4;
        const a = Math.atan2(dy, dx) - (spin * Math.PI) / 8;
        const k = Math.round((a / (Math.PI * 2)) * spokes);
        const off = Math.abs(Math.sin(a - (k / spokes) * Math.PI * 2)) * r;
        if (off < 0.75) c.set(x, y, blur && k % 2 ? 'U' : 'Y');
      }
    // The stock: copper, a brass butt plate and a brass band.
    box(c, 5, 16, 22, 25, [2, 1], byRow(16, 25, COPPER, 1, 1), 'l');
    box(c, 5, 17, 7, 24, [1], (_x, y) => (y < 19 ? 'y' : y > 22 ? 'U' : 'Y'), 'l');
    box(c, 12, 16, 13, 25, [], (x, y) => (y === 16 ? 'y' : y === 25 ? 'U' : x === 12 ? 'Y' : 'U'));
    // The body: a black iron housing for the works, lit along its top, edged in brass, brass rivets.
    box(c, 22, 13, 46, 26, [2, 1], byRow(13, 26, IRON, 2, 3), 'r');
    c.hline(24, 44, 13, 'Y').hline(23, 45, 24, 'U');
    dots(c, 'y', [
      [25, 16],
      [44, 16],
      [25, 21],
      [44, 21],
    ]);
    // The steam vent on its back: a short pipe, its cap.
    box(c, 40, 9, 42, 12, [], (x) => (x === 40 ? 'y' : x === 42 ? 'U' : 'Y'));
    c.hline(39, 43, 8, 'U').hline(39, 43, 7, 'Y');
    // The gauge: an ivory face in a dark ring, its marks, the red at its end, its needle.
    const [gx, gy] = GAUGE;
    for (let y = 14; y <= 25; y++)
      for (let x = 32; x <= 43; x++) {
        const [dx, dy] = [x + 0.5 - gx, y + 0.5 - gy];
        const r = Math.hypot(dx, dy);
        if (r > 4.6) continue;
        if (r > 3.5) c.set(x, y, dx + dy < -2 ? 'y' : dx + dy > 2 ? 'U' : 'Y');
        else c.set(x, y, dx + dy < -2.5 ? 'w' : 'e');
      }
    dots(c, 'r', [[40, 18]]);
    const a = -Math.PI / 2 + needle * 0.55;
    for (let t = 0.4; t <= 2.8; t += 0.4)
      c.set(Math.floor(gx + Math.cos(a) * t), Math.floor(gy + Math.sin(a) * t), needle >= 3 ? 'r' : 'k');
    c.set(Math.floor(gx), Math.floor(gy), 'N');
    // The trigger.
    box(c, 31, 27, 34, 29, [0, 1], (_x, y) => (y === 27 ? 'Y' : 'U'), 'r');
    // The barrel: brass narrowing to the muzzle, bound in copper, a small belled lip at its end (no taller than the
    // barrel's foot).
    taper(c, 47, 58, 19.5, 4.4, 2.8, round(BRASS));
    for (const x of [49, 50, 55]) taper(c, x, x, 19.5, 4.6 - (x - 47) * 0.13, 4.6 - (x - 47) * 0.13, round(COPPER));
    for (let x = 58; x <= 61; x++) {
      const h = 2.8 + (x - 58) * 0.35;
      taper(c, x, x, 19.5, h, h, (top, bottom) => (x === 61 ? 'n' : top === 0 ? 'y' : bottom === 0 ? 'U' : 'Y'));
    }
  });

export default sprite({
  name: 'pollinator_chronomancer',
  ...geometry,
  notes: `Tick-Tock (ADR-0160 amended, the Chronomancer set's gun): a brass blunderbuss with clockwork in it: a copper
stock bound in brass, a black iron housing edged in brass (the brass and the ivory pop on it, and it parts the gun
from the gold head at 1:1), a pressure gauge on its side (an ivory face in a brass rim, the red at its end, its needle
trembling over 4 frames), a steam vent on its back, a copper flywheel standing over it whose four brass
spokes turn a quarter of a quarter a frame (seamless), a brass barrel bound in copper narrowing to a small belled lip.
Fire: the flywheel kicked round (a blur of eight spokes), the needle thrown to the red, then back. Its flair: a puff of
steam off the vent every second or so, a warm light and a glint on the wheel. Rubric (4×, and 1:1 held on grass, lane
and night): S4 R4 P4 L4 C4 A4 F4. Weakest: the copper stock is a plain slab behind the fist; the gauge's needle is 2 px
at 1:1, its tremble reads only from ×2 up.`,
  anims: {
    idle: { fps: 6, frames: [ticktock(0, 0), ticktock(1, 0.4), ticktock(2, 0), ticktock(3, -0.4)] },
    fire: {
      fps: 20,
      loop: false,
      frames: [ticktock(1, 3, true), ticktock(3, 3, true), ticktock(2, 1.5), ticktock(0, 0)],
    },
  },
});
